import { useQuery } from '@tanstack/react-query'
import {
  Blocks,
  ExternalLink,
  Globe,
  CheckCircle2,
  Circle,
  KeyRound,
  ListChecks,
  PauseCircle,
  PlayCircle,
  Send,
  UserRound,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { errorMessage, toApiError } from '@/api/errors'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { DescriptionList } from '@/components/ui/DescriptionList'
import { Dialog } from '@/components/ui/Dialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { Textarea } from '@/components/ui/Textarea'
import {
  schoolQuery,
  settingsQuery,
  useReactivateSchool,
  useReinvite,
  useSetAddress,
  useSetModules,
  useSuspendSchool,
} from '@/features/platform/api'
import { SuffixInput } from '@/features/platform/components/SuffixInput'
import { ModulePicker } from '@/features/platform/components/ModulePicker'
import { SCHOOL_MODULES, type SchoolModuleKey } from '@/features/school/modules'
import { InvitationLink } from '@/features/platform/components/InvitationLink'
import { SchoolStatusBadge } from '@/features/platform/components/StatusBadge'
import type { Invitation, PlatformSchoolDetail } from '@/features/platform/types'
import { SETUP_STEPS } from '@/features/setup/steps'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { formatDate, formatDateTime, formatNumber } from '@/utils/format'

export function PlatformSchoolPage() {
  const { t } = useTranslation('platform')
  const id = Number(useParams().id)
  const school = useQuery(schoolQuery(id))
  useDocumentTitle(school.data?.name ?? 'School')

  if (school.isPending) return <LoadingState />
  if (school.error)
    return <ErrorState description={errorMessage(school.error)} onRetry={() => void school.refetch()} />
  const s = school.data
  return (
    <>
      <PageHeader
        title={s.name}
        description={[s.shortName, s.email, s.phone].filter(Boolean).join(' · ') || undefined}
        breadcrumbs={[{ label: t('Schools'), to: '/platform' }, { label: s.name }]}
        homeHref="/platform"
        actions={
          <div className="flex items-center gap-3">
            <SchoolStatusBadge status={s.status} />
            <StatusActions school={s} />
          </div>
        }
      />
      {s.status === 'suspended' ? (
        <Alert
          tone="danger"
          title={t('Suspended on {{date}}', { date: formatDate(s.suspendedAt) })}
          className="mb-6"
        >
          {s.suspensionReason ?? t('No reason given.')}{' '}
          {t('Nobody from this school can sign in until it is reactivated; its data is kept.')}
        </Alert>
      ) : null}
      <div className="grid items-start gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <AddressCard key={s.subdomain ?? ''} school={s} />
          <AdminCard school={s} />
          <ModulesCard key={s.modules.join()} school={s} />
          <SetupCard school={s} />
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader title={t('Usage')} />
            <CardBody>
              <DescriptionList
                className="sm:grid-cols-1"
                items={[
                  { label: t('Active students'), value: formatNumber(s.counts.students) },
                  { label: t('Active staff'), value: formatNumber(s.counts.staff) },
                  { label: t('User accounts'), value: formatNumber(s.counts.users) },
                  {
                    label: t('Last sign-in'),
                    value: s.lastActivityAt ? formatDateTime(s.lastActivityAt) : '—',
                  },
                ]}
              />
            </CardBody>
          </Card>
          <Card>
            <CardHeader title={t('Settings')} />
            <CardBody>
              <DescriptionList
                className="sm:grid-cols-1"
                items={[
                  { label: t('Country'), value: s.countryCode ?? '—' },
                  { label: t('Currency'), value: s.currency },
                  { label: t('Time zone'), value: s.timezone },
                  { label: t('Language'), value: s.locale === 'fr' ? 'Français' : 'English' },
                  { label: t('Created'), value: formatDateTime(s.createdAt) },
                ]}
              />
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  )
}

function StatusActions({ school }: { school: PlatformSchoolDetail }) {
  const { t } = useTranslation('platform')
  const [suspending, setSuspending] = useState(false)
  const reactivate = useReactivateSchool(school.id)
  if (school.status === 'suspended') {
    return (
      <Button
        leftIcon={<PlayCircle />}
        loading={reactivate.isPending}
        onClick={() =>
          reactivate.mutate(undefined, {
            onSuccess: () => toast.success(t('{{name}} is active again', { name: school.name })),
            onError: (e) => toast.error(t('Could not reactivate the school'), errorMessage(e)),
          })
        }
      >
        {t('Reactivate')}
      </Button>
    )
  }
  return (
    <>
      <Button variant="danger-soft" leftIcon={<PauseCircle />} onClick={() => setSuspending(true)}>
        {t('Suspend')}
      </Button>
      {suspending ? <SuspendDialog school={school} onOpenChange={setSuspending} /> : null}
    </>
  )
}

function SuspendDialog({
  school,
  onOpenChange,
}: {
  school: PlatformSchoolDetail
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation('platform')
  const suspend = useSuspendSchool(school.id)
  const [reason, setReason] = useState('')
  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="sm"
      icon={<PauseCircle />}
      title={t('Suspend {{name}}?', { name: school.name })}
      description={t(
        'Everyone from this school is signed out and cannot sign in until you reactivate it. No data is deleted.',
      )}
      dismissible={!suspend.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={suspend.isPending}>
            {t('Cancel')}
          </Button>
          <Button
            variant="danger"
            loading={suspend.isPending}
            onClick={() =>
              suspend.mutate(reason.trim() || null, {
                onSuccess: () => {
                  toast.success(t('{{name}} is suspended', { name: school.name }))
                  onOpenChange(false)
                },
                onError: (e) => toast.error(t('Could not suspend the school'), errorMessage(e)),
              })
            }
          >
            {t('Suspend')}
          </Button>
        </>
      }
    >
      <FormField label={t('Reason')} hint={t('Visible to operators only.')}>
        <Textarea rows={2} maxLength={255} value={reason} onChange={(e) => setReason(e.target.value)} />
      </FormField>
    </Dialog>
  )
}

function AdminCard({ school }: { school: PlatformSchoolDetail }) {
  const { t } = useTranslation('platform')
  const reinvite = useReinvite(school.id)
  const [sent, setSent] = useState<Invitation | null>(null)
  const a = school.admin
  return (
    <Card>
      <CardHeader icon={<UserRound />} title={t('Administrator')} />
      <CardBody className="space-y-4">
        {!a ? (
          <p className="text-sm text-muted">{t('This school has no administrator account.')}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Avatar name={a.name} />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">{a.name}</p>
                <p className="truncate text-sm text-muted">{a.email}</p>
              </div>
              {a.hasPassword ? (
                <Badge tone="success">
                  <KeyRound aria-hidden="true" />
                  {a.lastLoginAt
                    ? t('Last sign-in {{date}}', { date: formatDateTime(a.lastLoginAt) })
                    : t('Password set')}
                </Badge>
              ) : (
                <Badge tone="info">{t('Has not chosen a password yet')}</Badge>
              )}
            </div>
            {!a.hasPassword ? (
              <div className="flex flex-col gap-3 rounded-control border border-dashed border-line p-3 sm:flex-row sm:items-center">
                <p className="flex-1 text-sm text-ink-soft">
                  {school.pendingInvitation
                    ? t('Invitation sent {{sent}}, valid until {{expires}}.', {
                        sent: formatDateTime(school.pendingInvitation.sentAt),
                        expires: formatDateTime(school.pendingInvitation.expiresAt),
                      })
                    : t('The last invitation has expired.')}
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Send />}
                  loading={reinvite.isPending}
                  disabled={school.status === 'suspended'}
                  onClick={() =>
                    reinvite.mutate(undefined, {
                      onSuccess: setSent,
                      onError: (e) => toast.error(t('Could not send the invitation'), errorMessage(e)),
                    })
                  }
                >
                  {t('Send a new invitation')}
                </Button>
              </div>
            ) : null}
            {sent ? <InvitationLink invitation={sent} email={a.email} /> : null}
          </>
        )}
      </CardBody>
    </Card>
  )
}

function AddressCard({ school }: { school: PlatformSchoolDetail }) {
  const { t } = useTranslation('platform')
  const settings = useQuery(settingsQuery())
  const save = useSetAddress(school.id)
  const [value, setValue] = useState(school.subdomain ?? '')
  const [error, setError] = useState<string | null>(null)
  const base = settings.data?.baseDomain
  const dirty = value.trim().toLowerCase() !== (school.subdomain ?? '')
  const submit = () => {
    setError(null)
    save.mutate(value.trim() || null, {
      onSuccess: () => toast.success(t('Address updated'), t('HTTPS follows within a few minutes.')),
      onError: (e) => {
        const apiError = toApiError(e)
        setError(apiError.violations[0]?.message ?? apiError.message)
      },
    })
  }
  return (
    <Card>
      <CardHeader
        icon={<Globe />}
        title={t('Address')}
        description={
          base
            ? t('Where the school’s staff, students and parents sign in.')
            : t('Set the base domain in Settings first.')
        }
        actions={
          school.url ? (
            <Button asChild size="sm" variant="secondary" rightIcon={<ExternalLink />}>
              <a href={school.url} target="_blank" rel="noreferrer">
                {t('Open')}
              </a>
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-start"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <FormField label={t('Address')} hideLabel error={error ?? undefined} className="flex-1">
            <SuffixInput
              suffix={base ? `.${base}` : null}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={!base}
            />
          </FormField>
          <Button type="submit" disabled={!dirty || !base} loading={save.isPending}>
            {t('Save')}
          </Button>
        </form>
        {dirty && school.subdomain ? (
          <p className="mt-2 text-xs text-warning-700">
            {t('The old address stops working; tell the school.')}
          </p>
        ) : null}
      </CardBody>
    </Card>
  )
}

function ModulesCard({ school }: { school: PlatformSchoolDetail }) {
  const { t } = useTranslation('platform')
  const save = useSetModules(school.id)
  const saved = SCHOOL_MODULES.filter((m) => school.modules.includes(m))
  const [draft, setDraft] = useState<SchoolModuleKey[]>(saved)
  const dirty = draft.join() !== saved.join()
  return (
    <Card>
      <CardHeader
        icon={<Blocks />}
        title={t('Modules')}
        description={t(
          '{{count}} of {{total}} optional modules on. Turning one off hides it for the whole school; its data is kept.',
          {
            count: saved.length,
            total: SCHOOL_MODULES.length,
          },
        )}
        actions={
          dirty ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setDraft(saved)} disabled={save.isPending}>
                {t('Cancel')}
              </Button>
              <Button
                size="sm"
                loading={save.isPending}
                onClick={() =>
                  save.mutate(draft, {
                    onSuccess: () => toast.success(t('Modules updated for {{name}}', { name: school.name })),
                    onError: (e) => toast.error(t('Could not update the modules'), errorMessage(e)),
                  })
                }
              >
                {t('Save')}
              </Button>
            </>
          ) : undefined
        }
      />
      <CardBody>
        <ModulePicker value={draft} onChange={setDraft} disabled={save.isPending} />
      </CardBody>
    </Card>
  )
}

function SetupCard({ school }: { school: PlatformSchoolDetail }) {
  const { t } = useTranslation('platform')
  const { t: ts } = useTranslation('setup')
  const { done, total } = school.setup
  return (
    <Card>
      <CardHeader
        icon={<ListChecks />}
        title={t('Set-up progress')}
        description={t('{{done}} of {{total}} essential steps done', { done, total })}
        actions={school.setup.complete ? <Badge tone="success">{t('Ready')}</Badge> : undefined}
      />
      <CardBody>
        <div className="mb-4 h-2 rounded-full bg-surface-sunken" aria-hidden="true">
          <div
            className="h-full rounded-full bg-brand-500"
            style={{ width: `${total ? (done / total) * 100 : 0}%` }}
          />
        </div>
        <ul className="grid gap-2 sm:grid-cols-2">
          {school.setup.steps.map((step) => (
            <li key={step.key} className="flex items-center gap-2 text-sm">
              {step.done ? (
                <CheckCircle2 className="size-4 shrink-0 text-success-600" aria-hidden="true" />
              ) : (
                <Circle className="size-4 shrink-0 text-subtle" aria-hidden="true" />
              )}
              <span className={cn(step.done ? 'text-ink' : 'text-ink-soft')}>
                {ts(SETUP_STEPS[step.key].title)}
                {step.optional ? <span className="text-muted"> · {t('optional')}</span> : null}
              </span>
              <span className="sr-only">{step.done ? t('done') : t('to do')}</span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}
